'use client';

import React, { useState, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import Toast from '@atoms/Toast';
import Button from '@atoms/new/button';
import { getAuth } from '@services/identity.service';
import { logsToAwsCloudWatch } from 'services/logger.service';
import { clearAllAppData } from '@services/cleanup.service';

function ErrorFallback({ resetErrorBoundary, retryCount, setRetryCount }) {
  const handleRetry = () => {
    if (retryCount === 0) {
      resetErrorBoundary();
    } else if (retryCount > 0) {
      clearAllAppData();
      window.location.reload();
    }
    setRetryCount((prev) => prev + 1);
  };

  return (
    <div className="fixed bottom-8 left-8 z-9999 animate-slideLeft">
      <Toast
        variant="alert"
        width="w-[400px]"
        showIcon
        header={retryCount === 0 ? 'Something went wrong on our side' : 'Sorry, something still isn\'t right'}
        headerClassName="font-semibold"
        onClose={resetErrorBoundary}
        body={
          <div className="flex gap-4 flex-col">
            <p>
              {retryCount === 0
                ? 'We weren\'t able to load all the required modules. This is a temporary issue and is usually fixed by a quick refresh.'
                : 'We\'re not getting the right response from our server. We\'ll try clearing cookies and other data which will log you out but should resolve the issue.'}
            </p>
            <Button
              onClick={handleRetry}
              kind="tertiary"
              aria-label="Retry or refresh"
              className="max-w-fit dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-grey-900"
            >
              {retryCount === 0 ? 'Try again' : 'Clear site data'}
            </Button>
          </div>
        }
        position="bottom-left"
      />
    </div>
  );
}

export default function ErrorBoundaryWrapper({ children }) {
  const [retryCount, setRetryCount] = useState(0);

  const handleError = (error, errorInfo) => {
    const auth = getAuth();
    logsToAwsCloudWatch({
      logMessage: 'BrowserError(error boundary wrapper): Error occurred in the component',
      logData: {
        retryCount,
        message: error.message,
        userId: auth?.id || '',
        errorStack: error.stack,
        componentStack: errorInfo.componentStack,
      },
      level: 'ERROR',
    });
  };

  const fallbackRenderer = useCallback(({ resetErrorBoundary }) => (
    <ErrorFallback
      resetErrorBoundary={resetErrorBoundary}
      retryCount={retryCount}
      setRetryCount={setRetryCount}
    />
  ), [retryCount]);

  return (
    <ErrorBoundary
      fallbackRender={fallbackRenderer}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
}
